import React, { useEffect } from 'react';

/**
 * SuccessModal - Handles BOTH success and rejection states
 * 
 * Success: Shows congratulations, points earned, encouraging messages
 * Rejection: Shows what went wrong and how to fix it
 */
const SuccessModal = ({ isVisible, currentUser, successData, onDone, onViewLeaderboard, onScanAnother, consecutiveScans = 1 }) => {
  
  // Determine if this is a rejection based on successData
  const isRejection = successData?.isRejection || 
                      successData?.is_valid_tag === false || 
                      successData?.inspection?.is_valid_tag === false ||
                      successData?.status === 'rejected';
  
  const rejectionReason = successData?.validation_reason || 
                          successData?.inspection?.validation_reason || 
                          successData?.rejection_reason ||
                          'Image could not be processed';
  
  // Auto-dismiss after 0.8s for SUCCESS only (not rejections - user needs to read)
  useEffect(() => {
    if (!isVisible || !successData) return;
    
    // Don't auto-dismiss rejections - user needs to read the message
    if (isRejection) {
      console.log('⚠️ Rejection modal - NOT auto-dismissing (user needs to read)');
      return;
    }
    
    const timer = setTimeout(() => {
      console.log('✅ Success modal auto-dismissing after 0.8s (fast workflow mode)');
      if (onDone) onDone();
    }, 800);
    
    return () => clearTimeout(timer);
  }, [isVisible, successData, onDone, isRejection]);
  
  if (!isVisible || !successData) return null;

  // Escalating encouraging phrases based on consecutive scans 🔥
  const getEncouragingPhrase = (count) => {
    const phrases = [
      { min: 1, max: 1, text: "Nice work!", emoji: "👍", color: "text-green-600" },
      { min: 2, max: 2, text: "You're heating up!", emoji: "🔥", color: "text-orange-500" },
      { min: 3, max: 3, text: "You're on fire!", emoji: "🔥🔥", color: "text-orange-600" },
      { min: 4, max: 4, text: "Blazing through!", emoji: "🔥🔥🔥", color: "text-red-500" },
      { min: 5, max: 5, text: "Safety legend!", emoji: "⭐", color: "text-yellow-500" },
      { min: 6, max: 999, text: "UNSTOPPABLE!", emoji: "🚀", color: "text-purple-600" }
    ];
    
    const phrase = phrases.find(p => count >= p.min && count <= p.max) || phrases[phrases.length - 1];
    return phrase;
  };

  // Get user-friendly rejection message based on reason
  const getRejectionMessage = (reason) => {
    const reasonLower = (reason || '').toLowerCase();
    
    if (reasonLower.includes('blur') || reasonLower.includes('focus')) {
      return {
        title: "Image Too Blurry",
        message: "The photo was too blurry to read. Try holding your phone steady and make sure the tag is in focus.",
        tip: "Tip: Tap on the tag to focus before capturing"
      };
    }
    if (reasonLower.includes('dark') || reasonLower.includes('lighting') || reasonLower.includes('light')) {
      return {
        title: "Poor Lighting",
        message: "The image was too dark or had poor lighting. Try moving to a brighter area.",
        tip: "Tip: Natural light or overhead lights work best"
      };
    }
    if (reasonLower.includes('not a tag') || reasonLower.includes('no tag') || reasonLower.includes('invalid')) {
      return {
        title: "No Tag Detected",
        message: "We couldn't find a fire extinguisher inspection tag in this image.",
        tip: "Tip: Make sure the entire tag is visible in the frame"
      };
    }
    if (reasonLower.includes('angle') || reasonLower.includes('perspective')) {
      return {
        title: "Bad Angle",
        message: "The tag was captured at a difficult angle. Try photographing it straight-on.",
        tip: "Tip: Hold your phone parallel to the tag"
      };
    }
    if (reasonLower.includes('partial') || reasonLower.includes('cropped') || reasonLower.includes('cut off')) {
      return {
        title: "Tag Partially Visible",
        message: "Part of the tag was cut off. Make sure the entire tag fits in the frame.",
        tip: "Tip: Step back slightly to capture the full tag"
      };
    }
    
    // Default fallback
    return {
      title: "Image Issue",
      message: reason || "We couldn't process this image. Please try again with a clearer photo.",
      tip: "Tip: Center the tag in the frame and hold steady"
    };
  };

  const encouragement = getEncouragingPhrase(consecutiveScans);
  const rejection = isRejection ? getRejectionMessage(rejectionReason) : null;

  // ============================================================================
  // REJECTION STATE
  // ============================================================================
  if (isRejection) {
    return (
      <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
        <div className="bg-white backdrop-blur-md rounded-xl p-6 border border-solid border-gray-300 max-w-lg w-full relative shadow-2xl">
          {/* Rejection Icon - Yellow Warning */}
          <div className="flex justify-center mb-6">
            <div className="w-24 h-24 rounded-full bg-yellow-500/20 flex items-center justify-center">
              <svg className="w-16 h-16 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
          </div>
          
          {/* Rejection Title */}
          <h2 className="text-2xl font-bold text-[var(--pbfs-navy)] mb-2 text-center">
            Oops! Try Again
          </h2>
          
          {/* Problem Description */}
          <h3 className="text-xl font-bold text-yellow-600 mb-4 text-center">
            {rejection.title}
          </h3>
          
          {/* Explanation */}
          <div className="text-[var(--pbfs-navy)] text-base mb-4 text-center space-y-3">
            <p className="text-gray-700">
              {rejection.message}
            </p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-blue-700 text-sm">
                💡 {rejection.tip}
              </p>
            </div>
          </div>
          
          {/* No Points Message */}
          <p className="text-gray-500 text-sm text-center mb-6">
            No points were awarded for this scan.
          </p>
          
          {/* Action Buttons */}
          <div className="flex flex-col space-y-3 mt-6">
            {/* PRIMARY: Try Again */}
            <button
              onClick={onScanAnother}
              className="w-full bg-[var(--pbfs-green)] hover:bg-opacity-90 text-white font-bold py-4 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg text-lg"
            >
              📸 Try Again
            </button>
            
            {/* SECONDARY: Done / Skip */}
            <button
              onClick={onDone}
              className="w-full bg-gray-200 hover:bg-gray-300 border border-gray-300 text-[var(--pbfs-navy)] font-medium py-3 px-6 rounded-lg transition-all duration-300"
            >
              Skip for Now
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================================
  // SUCCESS STATE (Original)
  // ============================================================================
  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-white backdrop-blur-md rounded-xl p-6 border border-solid border-gray-300 max-w-lg w-full relative shadow-2xl">
        {/* Professional Success Icon */}
        <div className="flex justify-center mb-6">
          <div className="w-24 h-24 rounded-full bg-[var(--pbfs-green)]/20 flex items-center justify-center">
            <svg className="w-16 h-16 text-[var(--pbfs-green)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>
        
        {/* Personalized Greeting with Encouraging Phrase */}
        <h2 className="text-2xl font-bold text-[var(--pbfs-navy)] mb-2 text-center">
          Congratulations, {currentUser}!
        </h2>
        
        {/* Dynamic Encouraging Phrase */}
        {consecutiveScans > 1 && (
          <div className={`text-xl font-bold ${encouragement.color} mb-3 text-center animate-pulse`}>
            {encouragement.emoji} {encouragement.text}
          </div>
        )}
        
        {/* Main Title */}
        <h3 className="text-3xl font-bold text-[var(--pbfs-green)] mb-4 text-center">
          TAG CAPTURED!
        </h3>
        
        {/* Body Text */}
        <div className="text-[var(--pbfs-navy)] text-base mb-6 text-center space-y-2">
          <p>
            Risk extinguished! You earned <span className="font-bold text-[var(--pbfs-green)]">+1 POINT</span> for TagQuest!
          </p>
          <p className="text-gray-600">
            Keep scanning to climb the leaderboard!
          </p>
        </div>
        
        {/* Action Buttons - Vertical Stack */}
        <div className="flex flex-col space-y-3 mt-6">
          {/* PRIMARY: Done Button */}
          <button
            onClick={onDone}
            className="w-full bg-[var(--pbfs-green)] hover:bg-opacity-90 text-white font-bold py-4 px-6 rounded-lg transition-all duration-300 transform hover:scale-105 shadow-lg text-lg"
          >
            ✅ Done!
          </button>
          
          {/* SECONDARY 1: View Leaderboard */}
          <button
            onClick={onViewLeaderboard}
            className="w-full bg-gray-200 hover:bg-gray-300 border border-gray-300 hover:border-yellow-500/50 text-[var(--pbfs-navy)] font-medium py-3 px-6 rounded-lg transition-all duration-300 flex items-center justify-center space-x-2"
          >
            <span className="text-yellow-500">🏆</span>
            <span>View Leaderboard</span>
          </button>
          
          {/* SECONDARY 2: Scan Another */}
          <button
            onClick={onScanAnother}
            className="w-full bg-gray-200 hover:bg-gray-300 border border-gray-300 hover:border-green-500/50 text-[var(--pbfs-navy)] font-medium py-3 px-6 rounded-lg transition-all duration-300 flex items-center justify-center space-x-2"
          >
            <span className="text-[var(--pbfs-green)]">📸</span>
            <span>Scan Another</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SuccessModal;
